import { useState, useEffect, useCallback, useRef } from 'react';
import { Megaphone, Plus, Play, Pause, X, Trash2, Eye, Clock, CheckCircle, XCircle, Users, Send, Image, AlertCircle, RefreshCw, Upload, Link, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Marketing() {
  const [campaigns, setCampaigns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [imageInputType, setImageInputType] = useState('url'); // 'url' ou 'upload'
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    image_url: '',
    target_type: 'all',
    target_groups: [],
    target_clients: []
  });
  
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setGroups(await response.json());
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.filter(c => !c.archived));
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchGroups();
    fetchClients();
  }, [fetchCampaigns]);

  // Polling para campanhas em execução
  useEffect(() => {
    const runningCampaigns = campaigns.filter(c => c.status === 'running');
    if (runningCampaigns.length === 0) return;

    const interval = setInterval(() => {
      fetchCampaigns();
    }, 5000);

    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns]);

  // Polling para progresso da campanha selecionada
  useEffect(() => {
    if (!selectedCampaign || selectedCampaign.status !== 'running') return;

    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/marketing/campaigns/${selectedCampaign.id}/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setCampaignProgress(await response.json());
        }
      } catch (error) {
        console.error('Erro ao carregar progresso:', error);
      }
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 3000);
    return () => clearInterval(interval);
  }, [selectedCampaign]);

  const fetchPreview = async () => {
    if (formData.target_type === 'all' || 
        (formData.target_type === 'groups' && formData.target_groups.length > 0) ||
        (formData.target_type === 'clients' && formData.target_clients.length > 0)) {
      
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          target_type: formData.target_type,
          target_groups: formData.target_groups.join(','),
          target_clients: formData.target_clients.join(',')
        });
        
        const response = await fetch(`${API_URL}/api/marketing/preview-recipients?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          setPreviewData(await response.json());
        }
      } catch (error) {
        console.error('Erro ao carregar preview:', error);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  useEffect(() => {
    if (showCreateDialog) {
      fetchPreview();
    }
  }, [formData.target_type, formData.target_groups, formData.target_clients, showCreateDialog]);

  // Função para fazer upload de imagem
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      // Converter para base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result;
        if (base64) {
          setImagePreview(base64);
          setFormData({ ...formData, image_url: base64 });
          toast.success('Imagem carregada com sucesso!');
        }
        setUploadingImage(false);
      };
      reader.onerror = () => {
        toast.error('Erro ao carregar imagem');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao fazer upload da imagem');
      setUploadingImage(false);
    }
  };

  // Limpar imagem
  const clearImage = () => {
    setFormData({ ...formData, image_url: '' });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Reaproveitar campanha existente: preenche o formulário com os dados (gera novo nome)
  const handleReuseCampaign = (campaign) => {
    const baseName = (campaign.name || '').replace(/\s*\(cópia.*\)\s*$/i, '');
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    setFormData({
      name: `${baseName} (cópia ${dd}/${mm})`,
      message: campaign.message || '',
      image_url: campaign.image_cleared ? '' : (campaign.image_url || ''),
      target_type: campaign.target_type || 'all',
      target_groups: Array.isArray(campaign.target_groups) ? campaign.target_groups : [],
      target_clients: Array.isArray(campaign.target_clients) ? campaign.target_clients : [],
    });
    setImagePreview(campaign.image_cleared ? null : (campaign.image_url || null));
    setImageInputType(campaign.image_url && campaign.image_url.startsWith('data:') ? 'upload' : 'url');
    setShowCreateDialog(true);
    toast.success('Dados da campanha carregados. Revise e clique em Criar.');
  };

  const handleCreateCampaign = async () => {
    if (!formData.name.trim()) {
      toast.error('Digite um nome para a campanha');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Digite a mensagem da campanha');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Campanha criada com sucesso!');
        setShowCreateDialog(false);
        setFormData({
          name: '',
          message: '',
          image_url: '',
          target_type: 'all',
          target_groups: [],
          target_clients: []
        });
        setImagePreview(null);
        setImageInputType('url');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar campanha');
      }
    } catch (error) {
      toast.error('Erro ao criar campanha');
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Campanha iniciada!');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao iniciar campanha');
      }
    } catch (error) {
      toast.error('Erro ao iniciar campanha');
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Campanha pausada');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao pausar campanha');
      }
    } catch (error) {
      toast.error('Erro ao pausar campanha');
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta campanha?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Campanha cancelada');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao cancelar campanha');
      }
    } catch (error) {
      toast.error('Erro ao cancelar campanha');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Campanha excluída');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir campanha');
      }
    } catch (error) {
      toast.error('Erro ao excluir campanha');
    }
  };

  const viewCampaignDetails = async (campaign) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaign.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedCampaign(data);
        setShowDetailsDialog(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-600 text-slate-100',
      running: 'bg-blue-600 text-blue-100 animate-pulse',
      paused: 'bg-amber-600 text-amber-100',
      completed: 'bg-emerald-600 text-emerald-100',
      cancelled: 'bg-red-600 text-red-100',
      failed: 'bg-red-800 text-red-100'
    };
    const labels = {
      draft: 'Rascunho',
      running: 'Em execução',
      paused: 'Pausada',
      completed: 'Concluída',
      cancelled: 'Cancelada',
      failed: 'Falhou'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleGroupToggle = (groupId) => {
    setFormData(prev => ({
      ...prev,
      target_groups: prev.target_groups.includes(groupId)
        ? prev.target_groups.filter(id => id !== groupId)
        : [...prev.target_groups, groupId]
    }));
  };

  const handleClientToggle = (clientId) => {
    setFormData(prev => ({
      ...prev,
      target_clients: prev.target_clients.includes(clientId)
        ? prev.target_clients.filter(id => id !== clientId)
        : [...prev.target_clients, clientId]
    }));
  };

  const variablesList = [
    { var: '{nome}', desc: 'Nome do cliente' },
    { var: '{telefone}', desc: 'Telefone do cliente' },
    { var: '{produto}', desc: 'Nome do produto' },
    { var: '{valor}', desc: 'Valor do plano' },
    { var: '{vencimento}', desc: 'Data de vencimento' },
    { var: '{empresa}', desc: 'Nome da empresa' },
    { var: '{pix}', desc: 'Chave PIX' },
    { var: '{titular}', desc: 'Titular do PIX' }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="marketing-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-violet-500" />
              Marketing
            </h1>
            <p className="text-sm text-slate-400 mt-1">Envie mensagens em massa para seus clientes</p>
          </div>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white text-sm"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Campanha
          </Button>
        </div>

        {/* Warning Card */}
        <div className="mb-6 p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-amber-400 text-sm">Sistema Anti-Bloqueio</h3>
              <p className="text-xs text-slate-400 mt-1">
                As mensagens são enviadas com intervalo de <strong>1 minuto</strong> entre cada envio para evitar bloqueios do WhatsApp.
                Uma campanha com 50 clientes levará aproximadamente 50 minutos para ser concluída.
              </p>
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
              <Megaphone className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Nenhuma campanha</h3>
            <p className="text-slate-400 mb-4">Crie sua primeira campanha de marketing</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Campanha
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <div 
                key={campaign.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 md:p-6 hover:border-zinc-700 transition-all"
                data-testid={`campaign-card-${campaign.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    
                    <p className="text-sm text-slate-400 line-clamp-2 mb-3">{campaign.message}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-slate-400">
                        <Users className="w-4 h-4" />
                        <span>{campaign.total_recipients} destinatários</span>
                      </div>
                      
                      {campaign.status === 'running' && (
                        <div className="flex items-center gap-1 text-blue-400">
                          <Send className="w-4 h-4" />
                          <span>{campaign.sent_count}/{campaign.total_recipients} enviados</span>
                        </div>
                      )}
                      
                      {campaign.status === 'completed' && (
                        <>
                          <div className="flex items-center gap-1 text-emerald-400">
                            <CheckCircle className="w-4 h-4" />
                            <span>{campaign.success_count} sucesso</span>
                          </div>
                          {campaign.failed_count > 0 && (
                            <div className="flex items-center gap-1 text-red-400">
                              <XCircle className="w-4 h-4" />
                              <span>{campaign.failed_count} falhas</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {campaign.image_url && !campaign.image_cleared && (
                        <div className="flex items-center gap-1 text-violet-400">
                          <Image className="w-4 h-4" />
                          <span>Com imagem</span>
                        </div>
                      )}
                      
                      {campaign.image_cleared && (
                        <div className="flex items-center gap-1 text-slate-500">
                          <Image className="w-4 h-4" />
                          <span>Imagem removida</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Progress bar for running campaigns */}
                    {campaign.status === 'running' && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>Progresso</span>
                          <span>{Math.round((campaign.sent_count / campaign.total_recipients) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${(campaign.sent_count / campaign.total_recipients) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {campaign.status === 'draft' && (
                      <Button
                        onClick={() => handleStartCampaign(campaign.id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        size="sm"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Iniciar
                      </Button>
                    )}
                    
                    {campaign.status === 'running' && (
                      <Button
                        onClick={() => handlePauseCampaign(campaign.id)}
                        className="bg-amber-600 hover:bg-amber-700"
                        size="sm"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pausar
                      </Button>
                    )}
                    
                    {campaign.status === 'paused' && (
                      <>
                        <Button
                          onClick={() => handleStartCampaign(campaign.id)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          size="sm"
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Retomar
                        </Button>
                        <Button
                          onClick={() => handleCancelCampaign(campaign.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    
                    <Button
                      onClick={() => viewCampaignDetails(campaign)}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white"
                      data-testid={`view-campaign-${campaign.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    {campaign.status !== 'running' && (
                      <Button
                        onClick={() => handleReuseCampaign(campaign)}
                        variant="ghost"
                        size="sm"
                        className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
                        title="Reaproveitar campanha (editar e criar nova)"
                        data-testid={`reuse-campaign-${campaign.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {campaign.status !== 'running' && (
                      <Button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Campaign Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-violet-500" />
                Nova Campanha de Marketing
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Nome */}
              <div>
                <Label className="text-slate-300">Nome da Campanha</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Promoção de Natal"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="campaign-name-input"
                />
              </div>

              {/* Mensagem */}
              <div>
                <Label className="text-slate-300">Mensagem</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Digite sua mensagem aqui..."
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[120px]"
                  data-testid="campaign-message-input"
                />
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Variáveis disponíveis (clique para inserir):</p>
                  <div className="flex flex-wrap gap-1">
                    {variablesList.map(v => (
                      <button
                        key={v.var}
                        type="button"
                        onClick={() => setFormData({ ...formData, message: formData.message + v.var })}
                        className="px-2 py-1 text-xs bg-zinc-800 text-violet-400 rounded hover:bg-zinc-700"
                        title={v.desc}
                      >
                        {v.var}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Imagem - Upload ou URL */}
              <div>
                <Label className="text-slate-300 mb-2 block">Imagem (opcional)</Label>
                
                {/* Toggle entre URL e Upload */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => { setImageInputType('upload'); clearImage(); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      imageInputType === 'upload' 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-zinc-800 text-slate-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => { setImageInputType('url'); clearImage(); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      imageInputType === 'url' 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-zinc-800 text-slate-400 hover:bg-zinc-700'
                    }`}
                  >
                    <Link className="w-4 h-4" />
                    URL
                  </button>
                </div>

                {/* Upload de arquivo */}
                {imageInputType === 'upload' && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    
                    {!imagePreview ? (
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-violet-500 transition-colors"
                      >
                        {uploadingImage ? (
                          <div className="flex items-center gap-2 text-violet-400">
                            <RefreshCw className="w-6 h-6 animate-spin" />
                            <span>Carregando...</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-slate-500 mb-2" />
                            <span className="text-sm text-slate-400">Clique para selecionar</span>
                            <span className="text-xs text-slate-500 mt-1">ou arraste uma imagem aqui</span>
                            <span className="text-xs text-slate-600 mt-2">PNG, JPG até 5MB</span>
                          </>
                        )}
                      </label>
                    ) : (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full max-h-48 object-contain rounded-lg bg-zinc-800"
                        />
                        <button
                          type="button"
                          onClick={clearImage}
                          className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* URL da imagem */}
                {imageInputType === 'url' && (
                  <div>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://exemplo.com/imagem.jpg"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Cole uma URL de imagem pública</p>
                    
                    {/* Preview da URL */}
                    {formData.image_url && !formData.image_url.startsWith('data:') && (
                      <div className="mt-2 relative">
                        <img 
                          src={formData.image_url} 
                          alt="Preview" 
                          className="w-full max-h-32 object-contain rounded-lg bg-zinc-800"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Seleção de Destinatários */}
              <div>
                <Label className="text-slate-300">Destinatários</Label>
                <Select
                  value={formData.target_type}
                  onValueChange={(value) => setFormData({ ...formData, target_type: value, target_groups: [], target_clients: [] })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    <SelectItem value="groups">Selecionar por grupos</SelectItem>
                    <SelectItem value="clients">Selecionar clientes específicos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Seleção de Grupos */}
              {formData.target_type === 'groups' && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Selecione os grupos</Label>
                  <div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-zinc-800 rounded-lg">
                    {groups.length === 0 ? (
                      <p className="text-sm text-slate-500">Nenhum grupo cadastrado</p>
                    ) : (
                      groups.map(group => (
                        <div key={group.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`group-${group.id}`}
                            checked={formData.target_groups.includes(group.id)}
                            onCheckedChange={() => handleGroupToggle(group.id)}
                          />
                          <label htmlFor={`group-${group.id}`} className="text-sm text-slate-300 cursor-pointer">
                            {group.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Seleção de Clientes */}
              {formData.target_type === 'clients' && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Selecione os clientes</Label>
                  <div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-zinc-800 rounded-lg">
                    {clients.length === 0 ? (
                      <p className="text-sm text-slate-500">Nenhum cliente cadastrado</p>
                    ) : (
                      clients.map(client => (
                        <div key={client.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`client-${client.id}`}
                            checked={formData.target_clients.includes(client.id)}
                            onCheckedChange={() => handleClientToggle(client.id)}
                          />
                          <label htmlFor={`client-${client.id}`} className="text-sm text-slate-300 cursor-pointer">
                            {client.name} - {client.phone}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Preview */}
              {previewData && (
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Preview dos Destinatários</h4>
                    {previewLoading && <RefreshCw className="w-4 h-4 text-violet-400 animate-spin" />}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-emerald-400">
                      <Users className="w-4 h-4" />
                      <span>{previewData.total} clientes</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-400">
                      <Clock className="w-4 h-4" />
                      <span>~{previewData.estimated_time_minutes} minutos</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCreateDialog(false)}
                className="text-slate-400 hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateCampaign}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="save-campaign-btn"
              >
                Criar Campanha
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Campaign Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-violet-500" />
                Detalhes da Campanha
              </DialogTitle>
            </DialogHeader>

            {selectedCampaign && (
              <div className="space-y-6 py-4">
                {/* Info básica */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedCampaign.name}</h3>
                    <p className="text-sm text-slate-400 mt-1">{selectedCampaign.message}</p>
                  </div>
                  {getStatusBadge(selectedCampaign.status)}
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-zinc-800 rounded-lg text-center">
                    <Users className="w-6 h-6 text-violet-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedCampaign.total_recipients}</p>
                    <p className="text-xs text-slate-400">Total</p>
                  </div>
                  <div className="p-4 bg-zinc-800 rounded-lg text-center">
                    <Send className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedCampaign.sent_count}</p>
                    <p className="text-xs text-slate-400">Enviados</p>
                  </div>
                  <div className="p-4 bg-zinc-800 rounded-lg text-center">
                    <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedCampaign.success_count}</p>
                    <p className="text-xs text-slate-400">Sucesso</p>
                  </div>
                  <div className="p-4 bg-zinc-800 rounded-lg text-center">
                    <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{selectedCampaign.failed_count}</p>
                    <p className="text-xs text-slate-400">Falhas</p>
                  </div>
                </div>

                {/* Progress for running */}
                {selectedCampaign.status === 'running' && campaignProgress && (
                  <div className="p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="text-blue-400 font-medium">Em execução...</span>
                      <span className="text-white">{campaignProgress.progress_percent}%</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${campaignProgress.progress_percent}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Tempo estimado restante: ~{campaignProgress.estimated_minutes_remaining} minutos
                    </p>
                  </div>
                )}

                {/* Lista de destinatários */}
                {selectedCampaign.recipients && selectedCampaign.recipients.length > 0 && (
                  <div>
                    <h4 className="font-medium text-white mb-3">Destinatários ({selectedCampaign.recipients.length})</h4>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {selectedCampaign.recipients.map(recipient => (
                        <div 
                          key={recipient.id}
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg"
                        >
                          <div>
                            <p className="text-white text-sm">{recipient.client_name}</p>
                            <p className="text-xs text-slate-400">{recipient.client_phone}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {recipient.status === 'pending' && (
                              <span className="text-xs text-slate-400">Aguardando</span>
                            )}
                            {recipient.status === 'sent' && (
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            )}
                            {recipient.status === 'failed' && (
                              <div className="flex items-center gap-1">
                                <XCircle className="w-4 h-4 text-red-400" />
                                {recipient.error_message && (
                                  <span className="text-xs text-red-400 max-w-[150px] truncate" title={recipient.error_message}>
                                    {recipient.error_message}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              {selectedCampaign && selectedCampaign.status !== 'running' && (
                <Button
                  onClick={() => {
                    setShowDetailsDialog(false);
                    handleReuseCampaign(selectedCampaign);
                  }}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="reuse-campaign-details-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Reaproveitar
                </Button>
              )}
              <Button
                onClick={() => setShowDetailsDialog(false)}
                className="bg-zinc-800 hover:bg-zinc-700"
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
